import { useState } from "react";
import { toast } from "sonner";
import { Check, MessageCircle, Mail } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { fmtDate, fmtCurrency, niceName } from "@/lib/format";
import { DateInput } from "@/components/DateInput";
import { ContactDialog } from "@/components/ContactDialog";
import { waUrl } from "@/lib/contacts";
import { useAuth } from "@/context/AuthContext";
import { gpTrata } from "@/components/gpView";
import { twilio, sendWa } from "@/lib/whatsapp";

const today = () => new Date().toISOString().slice(0, 10);
const outline = "h-9 shrink-0 inline-flex items-center justify-center gap-2 px-3.5 rounded-lg text-sm font-semibold border border-slate-300 bg-white text-slate-800 hover:bg-slate-50 transition-colors";
const dark = "h-9 shrink-0 inline-flex items-center justify-center gap-2 px-3.5 rounded-lg text-sm font-semibold bg-ec-green text-white hover:bg-ec-greenDark transition-colors disabled:opacity-50";
const useRO = () => useAuth().user?.role === "direccao";
const TONE_BORDER = { grey: "border-slate-300", amber: "border-amber-500", red: "border-red-600" };
const TONE_TEXT = { ok: "text-slate-500", grey: "text-slate-500", amber: "text-amber-700", red: "text-red-700" };

export const scrollToSec = (k) => {
  const el = document.getElementById(`sec-${k}`);
  if (!el) return;
  el.scrollIntoView({ behavior: "smooth", block: "center" });
  el.classList.add("ring-2", "ring-amber-300");
  setTimeout(() => el.classList.remove("ring-2", "ring-amber-300"), 1600);
};

async function save(pid, body, onDone, msg) {
  try { await api.put(`/processes/${pid}/deed-checks`, body); if (msg) toast.success(msg); onDone(); }
  catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
}

function Box({ on, onClick, disabled, testid, tone = "grey" }) {
  return (
    <button type="button" onClick={onClick} disabled={disabled} aria-pressed={on} data-testid={testid} data-tone={on ? "ok" : tone}
      className={`grid place-items-center w-6 h-6 rounded-full border-2 shrink-0 transition-colors ${on ? "bg-green-700 border-green-700 text-white" : `${TONE_BORDER[tone] || TONE_BORDER.grey} bg-white enabled:hover:border-slate-500`} disabled:cursor-default`}>
      {on && <Check size={14} strokeWidth={3} />}
    </button>
  );
}

const SINAL_SRC = { cpcv: "Confirmado pelo CPCV", comprovativo: "Comprovativo validado" };

// Bloco CPCV: «Sinal recebido» (pelo CPCV, por comprovativo antigo ou assinalado à mão com data e valor).
export function SinalCheck({ p, onDone }) {
  const ro = useRO() || !!gpTrata(p) || p.status !== "em_venda";
  const s = p.deed?.sinal;
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState(today());
  const [amount, setAmount] = useState(s?.amount ?? "");
  if (!s || ["escriturado", "sem_venda"].includes(p.status)) return null;
  const detail = s.source === "manual" ? [fmtCurrency(s.amount), fmtDate(s.date), niceName(s.by)].filter(Boolean).join(" · ") : SINAL_SRC[s.source];
  const confirm = () => save(p.id, { sinal: true, sinal_date: date, sinal_amount: amount === "" ? null : Number(amount) }, () => { setOpen(false); onDone(); }, "Sinal recebido assinalado");
  return (
    <div className="col-span-2 pt-3 border-t border-slate-100" data-testid="sinal-check">
      <div className="flex items-start gap-3">
        <Box on={s.ok} disabled={ro || (s.ok && s.source !== "manual")} testid="sinal-box"
          onClick={() => (s.ok ? save(p.id, { sinal: false }, onDone, "Sinal desmarcado") : setOpen(!open))} />
        <div className="min-w-0">
          <div className="text-[15px] font-semibold navy-text">Sinal recebido</div>
          <div className="text-sm text-slate-500" data-testid="sinal-detail">{s.ok ? detail : `Por confirmar${s.amount ? ` · ${fmtCurrency(s.amount)}` : ""}`}</div>
        </div>
      </div>
      {open && !s.ok && (
        <div className="mt-3 space-y-2" data-testid="sinal-form">
          <div className="grid grid-cols-2 gap-2">
            <label className="text-xs text-slate-500">Recebido a<DateInput defaultValue={date} onChange={(e) => setDate(e.target.value)} className="mt-1" data-testid="sinal-date" /></label>
            <label className="text-xs text-slate-500">Valor (€)<input type="number" min="0" value={amount} onChange={(e) => setAmount(e.target.value)} className="mt-1 w-full h-10 rounded-md border border-slate-200 px-2 text-sm" data-testid="sinal-amount" /></label>
          </div>
          <div className="flex gap-2">
            <button onClick={confirm} disabled={!date} className={dark} data-testid="sinal-save">Assinalar</button>
            <button onClick={() => setOpen(false)} className="text-sm text-slate-500">Cancelar</button>
          </div>
        </div>
      )}
    </div>
  );
}

// Botão «Perguntar a …»: texto e destinatário vêm do servidor (business.deed_rows).
function AskButton({ ask, testid }) {
  if (!ask?.label) return null;
  const href = ask.phone ? `${waUrl(ask.phone)}?text=${encodeURIComponent(ask.text)}` : `mailto:${ask.email}?subject=${encodeURIComponent(ask.subject || "")}&body=${encodeURIComponent(ask.text)}`;
  if (ask.phone && twilio.on) {
    return <button type="button" onClick={() => sendWa(twilio.pid, ask.phone, ask.text)} className={outline} data-testid={testid}><MessageCircle size={15} /> {ask.label}</button>;
  }
  return <a href={href} target="_blank" rel="noreferrer" className={outline} data-testid={testid}>{ask.phone ? <MessageCircle size={15} /> : <Mail size={15} />} {ask.label}</a>;
}

function Row({ testid, box, label, badge, detail, tone, right, children }) {
  return (
    <div className="py-3 border-t border-slate-100" data-testid={testid}>
      <div className="flex flex-wrap items-start gap-3">
        {box}
        <div className="min-w-0 flex-1">
          <div className="text-[15px] font-semibold navy-text flex items-center gap-2">{label}{badge}</div>
          <div className={`text-sm ${TONE_TEXT[tone] || TONE_TEXT.grey}`} data-testid={`${testid}-detail`}>{detail}</div>
        </div>
        {right && <div className="basis-full pl-9 sm:basis-auto sm:pl-0">{right}</div>}
      </div>
      {children}
    </div>
  );
}

function ImpostosRow({ p, onDone, ro, row }) {
  const i = p.deed.impostos;
  const [pick, setPick] = useState(false);
  const [adding, setAdding] = useState(false);
  const sol = p.contacts?.solicitador;
  if (row.hidden) {
    return row.casa_pronta ? <div className="text-sm text-slate-500 py-3 border-t border-slate-100" data-testid="impostos-casa-pronta">Impostos (IMT e IS): pagos no balcão, no dia da escritura.</div> : null;
  }
  const by = (who, who_name) => save(p.id, { impostos: true, who, who_name }, () => { setPick(false); onDone(); }, "Impostos pagos assinalados");
  const right = !i.ok && !ro && (row.ask?.indicate
    ? <button onClick={() => setAdding(true)} className={outline} data-testid="impostos-indicate">Indicar quem trata da escritura</button>
    : <AskButton ask={row.ask} testid="impostos-ask" />);
  return (
    <Row testid="impostos-row" label="Impostos pagos (IMT e IS)" detail={row.detail} tone={row.tone} right={right}
      box={<Box on={i.ok} tone={row.tone} disabled={ro || (i.ok && i.source !== "manual")} testid="impostos-box"
        onClick={() => (i.ok ? save(p.id, { impostos: false }, onDone, "Impostos desmarcados") : setPick(!pick))} />}>
      {pick && !i.ok && (
        <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="impostos-who">
          <span className="text-sm text-slate-500">Quem confirmou?</span>
          <button onClick={() => by("consultor")} className={outline} data-testid="impostos-who-consultor">Eu</button>
          {sol?.name && <button onClick={() => by("solicitador", sol.name)} className={outline} data-testid="impostos-who-solicitador">{niceName(sol.name)}{sol.entity ? ` · ${sol.entity}` : ""}</button>}
        </div>
      )}
      <ContactDialog pid={p.id} role={adding ? "solicitador" : null} initial={sol} onClose={() => setAdding(false)} onSaved={() => { setAdding(false); onDone(); }} />
    </Row>
  );
}

function CreditRow({ p, onDone, ro, k, label, ok, row, needAmount }) {
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState(today());
  const [amount, setAmount] = useState("");
  const confirm = () => save(p.id, { [k]: true, [`${k}_date`]: date, ...(needAmount ? { [`${k}_amount`]: Number(amount) } : {}) }, () => { setOpen(false); onDone(); }, `${label} assinalado`);
  const badge = row.block && <span className="text-[10px] font-bold tracking-wider text-red-700 bg-red-50 rounded px-1.5 py-0.5" data-testid={`credit-${k}-block`}>BLOQUEIA</span>;
  const detail = <>{row.detail}{row.below && <span className="text-amber-700 font-semibold" data-testid="credit-below"> · {row.below}</span>}</>;
  return (
    <Row testid={`credit-${k}`} label={label} badge={badge} detail={detail} tone={row.tone} right={!ok && !ro && <AskButton ask={row.ask} testid={`credit-${k}-ask`} />}
      box={<Box on={ok} tone={row.tone} disabled={ro} testid={`credit-${k}-box`} onClick={() => (ok ? save(p.id, { [k]: false }, onDone, `${label} desmarcado`) : setOpen(!open))} />}>
      {open && !ok && (
        <div className="mt-3 flex flex-wrap items-end gap-2" data-testid={`credit-${k}-form`}>
          <label className="text-xs text-slate-500">Data<DateInput defaultValue={date} onChange={(e) => setDate(e.target.value)} className="mt-1" /></label>
          {needAmount && <label className="text-xs text-slate-500">Valor da avaliação (€)<input type="number" min="0" value={amount} onChange={(e) => setAmount(e.target.value)} className="mt-1 w-40 h-10 rounded-md border border-slate-200 px-2 text-sm block" data-testid={`credit-${k}-amount`} /></label>}
          <button onClick={confirm} disabled={!date || (needAmount && !(Number(amount) > 0))} className={dark} data-testid={`credit-${k}-save`}>Guardar</button>
        </div>
      )}
    </Row>
  );
}

// Gestor de crédito: opcional, nunca pedido nem contado.
function Manager({ p, onDone, ro }) {
  const [open, setOpen] = useState(false);
  const m = p.deed.rows?.manager;
  return (
    <>
      <button type="button" disabled={ro} onClick={() => setOpen(true)} className="text-sm text-slate-500 enabled:hover:text-slate-800 disabled:cursor-default" data-testid="credit-manager">
        {m ? `Gestor: ${[m.name, m.entity].filter(Boolean).join(" · ")}` : <>Gestor de crédito: <span className="underline underline-offset-2">+ adicionar</span></>}
      </button>
      <ContactDialog pid={p.id} role={open ? "banco_comprador" : null} initial={p.contacts?.banco_comprador} onClose={() => setOpen(false)} onSaved={() => { setOpen(false); onDone(); }} />
    </>
  );
}

export function CreditCard({ p, onDone }) {
  const ro = useRO() || !!gpTrata(p);
  const c = p.deed?.credit;
  const rows = p.deed?.rows;
  if (!c?.show || !rows || p.stage === "angariacao" || p.status !== "em_venda" || c.cpcv_signed || c.credito?.ok) return null;
  const owner = p.buyer_contact?.label || p.consultant_name;
  return (
    <div id="sec-credito" className="bg-white rounded-[16px] shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-slate-100 px-6 pt-5 pb-3 scroll-mt-24 transition-shadow" data-testid="credit-card">
      <div className="flex flex-wrap items-center gap-3 pb-3">
        <h2 className="font-heading text-2xl navy-text mr-auto">Crédito <span className="text-sm font-sans text-slate-500" data-testid="credit-owner">antes do CPCV{owner ? ` · ${owner}` : ""}</span></h2>
        {!ro && <AskButton ask={rows.ask_all} testid="credit-ask-all" />}
        <Manager p={p} onDone={onDone} ro={ro} />
      </div>
      <CreditRow p={p} onDone={onDone} ro={ro} k="pre_aprovacao" label="Pré-aprovação" ok={c.pre?.ok} row={rows.pre} />
      <CreditRow p={p} onDone={onDone} ro={ro} k="avaliacao" label="Avaliação feita" ok={c.avaliacao.ok} row={rows.avaliacao} needAmount />
      <CreditRow p={p} onDone={onDone} ro={ro} k="credito" label="Aprovação final" ok={c.credito.ok} row={rows.credito} />
    </div>
  );
}

// Bloco Escritura: onde é, sinal (só depois do CPCV assinado, se faltar) e impostos pagos.
export function EscrituraCard({ p, onDone }) {
  const ro = useRO() || !!gpTrata(p);
  if (!p.deed?.rows || p.stage === "angariacao" || p.status !== "em_venda") return null;
  const { rows } = p.deed;
  const s = rows.sinal;
  return (
    <div id="sec-escritura" className="bg-white rounded-[16px] shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-slate-100 px-6 pt-5 pb-3 scroll-mt-24 transition-shadow" data-testid="escritura-card">
      <div className="flex flex-wrap items-center gap-3 pb-3">
        <h2 className="font-heading text-2xl navy-text mr-auto">Escritura</h2>
      </div>
      {!s.hidden && (s.tone === "ok"
        ? <Row testid="sinal-ok" label="Sinal recebido" detail={s.detail} tone="ok" box={<Box on disabled testid="sinal-ok-box" />} />
        : <Row testid="sinal-missing" label={<span className="text-amber-800">Sinal por confirmar</span>} detail="" tone="amber" box={<Box tone="amber" disabled testid="sinal-missing-box" />}
            right={<button onClick={() => scrollToSec("cpcv")} className={outline} data-testid="sinal-goto-cpcv">Ver no CPCV</button>} />)}
      <ImpostosRow p={p} onDone={onDone} ro={ro} row={rows.impostos} />
      {p.deed.credit?.show && p.deed.credit?.cpcv_signed && <Row testid="credito-ok" label="Crédito aprovado" detail={rows.credito?.detail} tone="ok" box={<Box on disabled testid="credito-ok-box" />} />}
      {rows.banco && !rows.banco.hidden && (
        <Row testid="banco-row" label="Banco · data da escritura confirmada" detail={rows.banco.detail} tone={rows.banco.tone}
          box={<Box on={rows.banco.tone === "ok"} tone={rows.banco.tone} disabled={ro} testid="banco-box"
            onClick={() => save(p.id, { banco: rows.banco.tone !== "ok" }, onDone, rows.banco.tone === "ok" ? "Desmarcado" : "Banco confirmou a data")} />} />
      )}
    </div>
  );
}
